import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Send, Lock, Crown, ArrowLeft, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import SubscriptionRequired from '../components/SubscriptionRequired';

export default function ChatPage() {
  const { userId } = useParams();
  const { user, token, api } = useAuth();
  const [partner, setPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  const canChat = user?.permissions?.can_chat === true;

  useEffect(() => {
    if (!canChat) {
      setShowSubscriptionPopup(true);
      return;
    }
    
    fetchPartner();
    fetchHistory();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [userId, canChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchPartner = async () => {
    try {
      const response = await api.get(`/profile/${userId}`);
      setPartner(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    }
  };

  const fetchHistory = async () => {
    try {
      // Fetch from both collections and merge
      let chatMessages = [];
      let httpMessages = [];
      
      try {
        const chatRes = await api.get(`/chat/history/${userId}`);
        chatMessages = chatRes.data || [];
      } catch {}
      
      try {
        const httpRes = await api.get(`/messages/${userId}`);
        httpMessages = httpRes.data || [];
      } catch {}
      
      const allMessages = [...chatMessages, ...httpMessages];
      const uniqueMap = new Map();
      allMessages.forEach(m => { if (!uniqueMap.has(m.id)) uniqueMap.set(m.id, m); });
      const merged = Array.from(uniqueMap.values()).sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      
      setMessages(merged);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/${token}`);

    ws.onopen = () => {
      setConnected(true);
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WebSocket message received:', data.type);
      
      // Handle both message types:
      // - "message_sent" for messages we sent (confirmation)
      // - "new_message" for messages received from others
      if ((data.type === 'message_sent' || data.type === 'new_message') && 
          (data.sender_id === userId || data.recipient_id === userId)) {
        const messageData = { ...data };
        if (data.type === 'message_sent') messageData._isOwn = true;
        setMessages(prev => {
          // Avoid duplicates by checking message id
          if (prev.some(m => m.id === messageData.id)) {
            return prev;
          }
          return [...prev, messageData];
        });
      }
    };

    ws.onclose = () => {
      setConnected(false);
      console.log('WebSocket disconnected');
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !wsRef.current || !connected) return;

    wsRef.current.send(JSON.stringify({
      type: 'message',
      recipient_id: userId,
      content: newMessage.trim()
    }));

    setNewMessage('');
  };

  // Show subscription popup for non-subscribers
  if (!canChat) {
    return (
      <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="chat-page">
        <SubscriptionRequired 
          open={showSubscriptionPopup} 
          onClose={() => setShowSubscriptionPopup(false)} 
          feature="chat" 
        />
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          <div className="glass-card p-8 md:p-12">
            <Lock className="w-12 h-12 md:w-16 md:h-16 text-gold mx-auto mb-4 md:mb-6" />
            <h1 className="font-serif text-2xl md:text-3xl gold-gradient-text mb-4">
              Chat en direct
            </h1>
            <p className="text-gray-400 mb-2 text-sm md:text-base">
              Accédez à toutes les fonctionnalités de DiscreetLux
            </p>
            <p className="text-gray-500 text-xs md:text-sm mb-6">
              Photos, messagerie, chat en direct, recherche avancée...
            </p>
            <Link to="/subscription" className="btn-gold inline-flex items-center gap-2">
              <Crown className="w-4 h-4" />
              Abonnez-vous
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="chat-page">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="glass-card overflow-hidden h-[calc(100vh-160px)] md:h-[calc(100vh-160px)]">
          {/* Header */}
          <div className="p-3 md:p-4 border-b border-white/10 flex items-center gap-3 md:gap-4">
            <Link to="/messages" className="text-gray-400 hover:text-gold transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            {partner && (
              <Link to={`/member/${userId}`} className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar className="w-9 h-9 md:w-10 md:h-10 border border-gold/30 flex-shrink-0">
                  {partner.profile_picture ? (
                    <AvatarImage src={getImageUrl(partner.profile_picture)} />
                  ) : (
                    <AvatarFallback className="bg-dark-subtle text-gold text-sm">
                      {partner.pseudo?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="min-w-0">
                  <h3 className="font-semibold text-white text-sm md:text-base truncate">{partner.pseudo}</h3>
                  <span className={`text-xs ${connected ? 'text-green-400' : 'text-gray-500'}`}>
                    {connected ? 'En ligne' : 'Hors ligne'}
                  </span>
                </div>
              </Link>
            )}
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${connected ? 'bg-green-400' : 'bg-gray-500'}`} />
          </div>

          {/* Messages */}
          <ScrollArea className="h-[calc(100%-110px)] md:h-[calc(100%-130px)] p-3 md:p-4">
            <div className="space-y-3 md:space-y-4" data-testid="chat-messages">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <MessageCircle className="w-10 h-10 md:w-12 md:h-12 mb-4 opacity-50" />
                  <p className="text-sm md:text-base">Commencez la conversation</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`flex ${(msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo) ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] md:max-w-[70%] p-2.5 md:p-3 rounded-lg ${
                        (msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo)
                          ? 'bg-gold text-dark'
                          : 'bg-dark-subtle text-white'
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <span className={`text-xs mt-1 block ${
                        (msg._isOwn === true || msg.sender_id === user.id || msg.sender_pseudo === user.pseudo) ? 'text-dark/60' : 'text-gray-500'
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={connected ? 'Votre message...' : 'Connexion en cours...'}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="flex-1 bg-dark-subtle border-white/20 text-white"
                disabled={!connected}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                className="btn-gold"
                disabled={!connected || !newMessage.trim()}
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
